import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { AdminBuild, AuditEntry, PlatformOverview } from "@/lib/types";
import { Skeleton, TableSkeleton } from "@/ui/States";
import { Table, Td, Th, Tr } from "@/ui/Table";
import { Panel, SectionHead } from "@/ui/Panel";
import { PlatformSpine } from "@/features/platform/screens";
import { Reading } from "@/ui/Strip";
import { Button } from "@/ui/Button";
import { cx, humaniseAction, relativeTime } from "@/lib/format";

export const Route = createFileRoute("/_app/admin/")({
  component: PlatformScreen,
});

/**
 * The first thing an operator sees, built around the first question they ask.
 *
 * # WHAT IT WAS
 *
 * A stack of coloured banners, then six linked tiles, then two half-width
 * panels side by side — "Builds that failed" and "What staff have done". On a
 * 1440 by 1000 screen with a healthy installation, the left card said
 * "Nothing has failed." in grey, centred, and three hundred pixels of the page
 * below both panels were empty. The most-visited screen on the staff surface
 * spent its lower half proving there was nothing to show.
 *
 * # THE QUESTION
 *
 * Nobody opens this page to find out how many organisations exist. They open
 * it to find out whether anything is waiting on them, and then to see what has
 * been happening. So:
 *
 *   the queue     everything with a person at the other end of it, in one
 *                 list, in the order it should be dealt with
 *   the figures   the shape of the installation, as a rail rather than a page
 *   the record    what staff have done, full width, filling what is left
 *
 * # WHY THE QUEUE IS ONE LIST AND NOT FOUR BANNERS
 *
 * Four tinted boxes stacked on each other is four separate alarms, each the
 * same size whether it stands for one person waiting or fifty, and together
 * they are a wall of colour that stops meaning anything. One list, one row per
 * kind of work, the count set in the display face so size carries weight, and
 * the only colour is the dot. Which is the rule everywhere else in this
 * product: colour means state.
 *
 * # WHY THE RECORD IS LAST AND FULL WIDTH
 *
 * Because it is the one thing here that grows. A table of ten rows across the
 * full width ends the page on content instead of on whitespace, and it is also
 * how an operator answers "did somebody already deal with this" without
 * leaving the screen.
 */
function PlatformScreen() {
  const overview = useQuery({
    queryKey: ["platform-overview"],
    queryFn: ({ signal }) =>
      api.get<{ overview: PlatformOverview }>("/admin/overview", undefined, signal),
    refetchInterval: 30000,
  });

  // Failed builds are the most common thing a customer writes in about, so the
  // operator should see them before the customer does.
  const failed = useQuery({
    queryKey: ["platform-builds", "failed", "recent"],
    queryFn: ({ signal }) =>
      api.get<{ builds: AdminBuild[] | null }>(
        "/admin/builds",
        { status: "failed", limit: 6 },
        signal,
      ),
  });

  // Tickets whose next move is ours. The only count on this page with a
  // person waiting at the other end of it, and it was the one not here.
  const tickets = useQuery({
    queryKey: ["platform-tickets", "needs-us"],
    queryFn: ({ signal }) =>
      api.get<{ counts: { needs_us: number } }>(
        "/admin/tickets",
        { needs_us: true, limit: 1 },
        signal,
      ),
    refetchInterval: 60000,
  });

  const staff = useQuery({
    queryKey: ["staff-actions", "recent"],
    queryFn: ({ signal }) =>
      api.get<{ entries: AuditEntry[] | null }>(
        "/admin/audit-log",
        { staff_only: true, limit: 10 },
        signal,
      ),
  });

  const o = overview.data?.overview;
  const failedBuilds = failed.data?.builds ?? [];
  const staffActions = staff.data?.entries ?? [];

  /**
   * The shape of the installation, beside the work rather than above it.
   *
   * These counts answer "how big is this" — a question somebody asks once a
   * quarter — and they were a rail across the top, pushing the queue and the
   * record down the page. In the spine they are available without being in
   * the way, which is the right weight for them.
   */
  const Installation = () => (
    <PlatformSpine>
      {overview.isError ? (
        <Reading label="The installation">
          <span className="text-[var(--danger)]">unavailable</span>
        </Reading>
      ) : overview.isLoading || !o ? (
        Array.from({ length: 6 }, (_, i) => (
          <Reading key={i} label="">
            <Skeleton className="h-5 w-16" />
          </Reading>
        ))
      ) : (
        <>
          <Count label="Organisations" value={o.organisations} to="/admin/organisations"
            note={o.suspended_organisations > 0 ? `${o.suspended_organisations} suspended` : undefined} />
          <Count label="People" value={o.users} to="/admin/users" />
          <Count label="Services" value={o.services} to="/admin/services"
            note={`${o.running_services} running`} />
          <Count label="Databases" value={o.databases} to="/admin/databases" />
          <Count label="Builds today" value={o.builds_last_day} to="/admin/builds" />
          <Count label="Waiting" value={o.awaiting_access} to="/admin/access" />
        </>
      )}
    </PlatformSpine>
  );

  return (
    <div className="flex flex-col gap-9">
      <Installation />
      <Queue
        ready={Boolean(o) && !tickets.isLoading && !failed.isLoading}
        items={[
          {
            key: "tickets",
            count: tickets.data?.counts.needs_us ?? 0,
            to: "/admin/tickets",
            one: "support request is waiting on us",
            many: "support requests are waiting on us",
            note: "Either new, or the customer has answered and nobody has replied.",
            act: "Answer",
            tone: "degraded",
          },
          {
            key: "access",
            count: o ? o.awaiting_access : 0,
            to: "/admin/access",
            one: "person is waiting to be let in",
            many: "people are waiting to be let in",
            note: "They registered and are looking at a page that says so.",
            act: "Decide",
            tone: "degraded",
          },
          {
            key: "builds",
            count: failedBuilds.length,
            to: "/admin/builds",
            one: "recent build failed",
            many: "recent builds failed",
            note: "A failed build is the most common thing a customer writes in about.",
            act: "Look",
            tone: "failed",
          },
          {
            key: "suspended",
            count: o ? o.suspended_organisations : 0,
            to: "/admin/organisations",
            one: "organisation is suspended",
            many: "organisations are suspended",
            note: "A suspended account can read its own data and run nothing.",
            act: "Review",
            tone: "failed",
          },
        ]}
      />

      <section className="flex min-w-0 flex-col gap-3.5">
        <SectionHead
          title="What staff have done"
          actions={
            <Link to="/admin/staff">
              <Button tone="ghost" size="dense">
                All actions
              </Button>
            </Link>
          }
        />
        <Panel padded={false}>
          {staff.isLoading ? (
            <TableSkeleton rows={6} columns={4} />
          ) : staffActions.length === 0 ? (
            <p className="t-body px-5 py-8">
              Nothing yet. Every read and every change anybody from Drumlin
              makes to a customer account lands here.
            </p>
          ) : (
            // Percentages need a floor under them.
            //
            // `table-fixed` with percentage columns divides whatever width it
            // is given, including a phone's 358 — four columns of 57 pixels,
            // "2 minutes ago" over three lines, a table that fits perfectly
            // and says nothing. Below this width it scrolls inside its own
            // frame instead.
            <Table dense fixed minWidth="38rem">
              <thead>
                <Tr>
                  <Th width="w-[26%]">Action</Th>
                  <Th width="w-[32%]">On</Th>
                  <Th width="w-[26%]">By</Th>
                  <Th width="w-[16%]" align="right">When</Th>
                </Tr>
              </thead>
              <tbody>
                {staffActions.map((e) => (
                  <Tr key={e.id}>
                    <Td clip>
                      <span className="t-strong">{humaniseAction(e.action)}</span>
                    </Td>
                    <Td clip title={e.target_label ?? undefined}>
                      {e.target_label ?? "—"}
                    </Td>
                    <Td clip title={e.actor_label}>
                      {e.actor_label}
                    </Td>
                    <Td align="right" clip title={new Date(e.created_at).toLocaleString()}>
                      {relativeTime(e.created_at)}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          )}
        </Panel>
      </section>
    </div>
  );
}

/**
 * One figure on the rail.
 *
 * Every one links somewhere: the reason to show six numbers at all is that
 * one of them sends you to the screen behind it. The lift on hover is the
 * whole tile rather than a background change, because there is no tile any
 * more — the hover has to be the figure itself moving.
 */
function Count({
  label,
  value,
  note,
  to,
}: {
  label: string;
  value: number;
  note?: string;
  to: string;
}) {
  return (
    <Link
      to={to}
      className={cx(
        "group/count flex shrink-0 flex-col justify-center gap-0.5 px-5 first:pl-0",
        "min-w-28",
      )}
    >
      <span className="section-label">{label}</span>
      <span className="t-figure-sm truncate transition-colors group-hover/count:text-[var(--primary)]">
        {value.toLocaleString()}
      </span>
      {note && <span className="t-meta truncate leading-3">{note}</span>}
    </Link>
  );
}

/**
 * Everything with a person at the other end of it, in one list.
 *
 * # WHY ZEROES ARE NOT SHOWN
 *
 * Because "0 waiting, 0 failed, 0 suspended, 0 unanswered" is four things to
 * read and nothing to do, every day, which is how somebody learns to stop
 * reading the top of a page. A row appears when there is something behind it.
 * When none do, one sentence says so — which is worth stating once rather
 * than inferring from four absences.
 *
 * # WHY IT WAITS FOR ITS DATA
 *
 * So it cannot flash "nothing is waiting" and correct itself a second later.
 * Telling somebody everything is fine and then taking it back is worse than
 * telling them nothing yet.
 */
function Queue({
  items,
  ready,
}: {
  items: {
    key: string;
    count: number;
    to: string;
    one: string;
    many: string;
    note: string;
    act: string;
    tone: "degraded" | "failed";
  }[];
  ready: boolean;
}) {
  if (!ready) return <Skeleton className="h-[4.5rem] w-full rounded-[var(--radius)]" />;

  // Worst first, and within a tone the larger number first. An operator works
  // down this list, so its order is a claim about what to do next.
  const live = items
    .filter((i) => i.count > 0)
    .sort((a, b) =>
      a.tone === b.tone ? b.count - a.count : a.tone === "failed" ? -1 : 1,
    );

  if (live.length === 0) {
    return (
      <p className="t-body flex items-center gap-2.5 py-1">
        <span
          aria-hidden="true"
          className="size-1.5 shrink-0 rounded-full bg-[var(--success)]"
        />
        Nothing is waiting. Every support request has been answered, nobody is
        held at the door, no recent build failed and no organisation is
        suspended.
      </p>
    );
  }

  return (
    <Panel padded={false}>
      <ul>
        {live.map((item) => (
          <li key={item.key} className="border-b border-[var(--border)] last:border-b-0">
            <Link
              to={item.to}
              className={cx(
                "row-live group/q flex items-center gap-4 px-5 py-3.5",
                "focus-visible:outline-none",
              )}
            >
              <span
                aria-hidden="true"
                className="size-2 shrink-0 rounded-full"
                style={{ background: LAMP_FOR[item.tone] }}
              />
              {/* The count in the display face, so fifty looks like more work
                  than one. A row that reads the same at either size is a row
                  that has to be read rather than seen. */}
              <span
                className="t-figure shrink-0 tabular-nums"
                style={{ color: LAMP_FOR[item.tone] }}
              >
                {item.count}
              </span>
              <span className="min-w-0 flex-1">
                <span className="t-strong block">
                  {item.count === 1 ? item.one : item.many}
                </span>
                <span className="t-meta block">{item.note}</span>
              </span>
              <span
                className={cx(
                  "section-label shrink-0 whitespace-nowrap text-[var(--text-secondary)]",
                  "transition-[color,transform] duration-[var(--d-fast)]",
                  "group-hover/q:translate-x-0.5 group-hover/q:text-[var(--text-primary)]",
                )}
              >
                {item.act} &rarr;
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </Panel>
  );
}

/**
 * The lamp a state name lights.
 *
 * The screens used to compose a custom property name out of a variable —
 * `var(--color-state-${token}-fg)` — which works until the token set is
 * replaced and then fails silently, because a missing custom property is not
 * an error, it is simply nothing. This is a lookup, so an unknown state is a
 * compile-time concern rather than an invisible one.
 */
const LAMP_FOR: Record<string, string> = {
  running: "var(--success)",
  healthy: "var(--success)",
  degraded: "var(--warning)",
  suspended: "var(--warning)",
  failed: "var(--danger)",
  stopped: "var(--text-faint)",
  building: "var(--primary)",
};
